import { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { RefreshCw, ChevronLeft, Sparkles, Send, Mail } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

function timeAgo(iso) {
  if (!iso) return "";
  const s = (Date.now() - new Date(iso).getTime()) / 1000;
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  return `${Math.floor(s / 86400)}d`;
}

export default function MobileInbox() {
  const { reloadBadges } = useOutletContext() || {};
  const [rows, setRows] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [open, setOpen] = useState(null);       // detail object
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [reply, setReply] = useState("");
  const [drafting, setDrafting] = useState(false);
  const [sending, setSending] = useState(false);

  const load = async () => {
    try { const { data } = await api.get("/m365/inbox?lead_only=true&limit=50"); setRows(data.messages || []); }
    catch { setRows([]); }
  };
  useEffect(() => { load(); }, []);

  const refresh = async () => {
    setRefreshing(true);
    try { await api.post("/m365/poll"); } catch { /* poll optional */ }
    await load(); setRefreshing(false);
  };

  const openEmail = async (r) => {
    setLoadingDetail(true); setOpen({ ...r }); setReply("");
    try { const { data } = await api.get(`/m365/email/${r.id}`); setOpen(data); }
    catch { toast.error("Could not open email"); }
    setLoadingDetail(false);
  };

  const aiDraft = async () => {
    if (!open) return;
    setDrafting(true);
    try {
      const { data } = await api.post(`/m365/email/${open.id}/ai-reply`, { graph_message_id: open.graph_message_id });
      const html = data.draft_html || data.draft || "";
      // strip tags to plain text for the mobile editor
      const txt = html.replace(/<br\s*\/?>(?=)/gi, "\n").replace(/<\/p>/gi, "\n\n").replace(/<[^>]+>/g, "").trim();
      setReply(txt);
      toast.success("Draft ready — review & send");
    } catch (e) { toast.error(e?.response?.data?.detail || "Draft failed"); }
    setDrafting(false);
  };

  const send = async () => {
    if (!open || !reply.trim()) { toast.error("Write a reply first"); return; }
    setSending(true);
    try {
      const html = "<p>" + reply.replace(/\n\n/g, "</p><p>").replace(/\n/g, "<br>") + "</p>";
      await api.post(`/m365/email/${open.id}/send-reply`, {
        to: [open.from_email],
        html_body: html,
        prospect_id: open.prospect_id || undefined,
        graph_message_id: open.graph_message_id || undefined,
      });
      toast.success("Reply sent in-thread");
      setOpen(null); setReply("");
      await load(); reloadBadges?.();
    } catch (e) { toast.error(`Send failed: ${e?.response?.data?.detail || "unknown"}`); }
    setSending(false);
  };

  // ---- Detail view ----
  if (open) {
    return (
      <div className="space-y-3" data-testid="m-inbox-detail">
        <button onClick={() => setOpen(null)} className="flex items-center gap-1 text-sm text-muted-foreground" data-testid="m-inbox-back">
          <ChevronLeft className="w-4 h-4" /> Inbox
        </button>
        <div className="bg-card border border-border rounded-xl p-3.5">
          <div className="text-sm font-semibold">{open.prospect_name || open.from_email}</div>
          <div className="text-[0.7rem] text-muted-foreground">{open.from_email}{open.prospect_company ? ` · ${open.prospect_company}` : ""}</div>
          <div className="text-xs font-medium mt-2">{open.subject}</div>
          <div className="text-[0.78rem] text-muted-foreground mt-2 max-h-56 overflow-y-auto whitespace-pre-wrap break-words"
            dangerouslySetInnerHTML={{ __html: loadingDetail ? "Loading…" : (open.body || open.body_preview || "") }} />
        </div>

        <textarea value={reply} onChange={(e) => setReply(e.target.value)} rows={7}
          placeholder="Write your reply…"
          className="w-full text-sm bg-background border border-border rounded-xl p-3 resize-none focus:outline-none focus:border-signal-500"
          data-testid="m-inbox-reply" />

        <div className="grid grid-cols-2 gap-2">
          <button onClick={aiDraft} disabled={drafting}
            className="flex items-center justify-center gap-1.5 text-sm font-medium bg-card border border-border rounded-xl py-3 disabled:opacity-50"
            data-testid="m-inbox-ai-draft">
            <Sparkles className={`w-4 h-4 ${drafting ? "animate-pulse" : ""}`} /> {drafting ? "Drafting…" : "AI draft"}
          </button>
          <button onClick={send} disabled={sending || !reply.trim()}
            className="flex items-center justify-center gap-1.5 text-sm font-semibold bg-signal-500 text-background rounded-xl py-3 disabled:opacity-50"
            data-testid="m-inbox-send">
            <Send className="w-4 h-4" /> {sending ? "Sending…" : "Send reply"}
          </button>
        </div>
      </div>
    );
  }

  // ---- List view ----
  return (
    <div className="space-y-4" data-testid="m-inbox">
      <div className="flex items-center justify-between">
        <h1 className="text-lg font-semibold">Lead replies</h1>
        <button onClick={refresh} disabled={refreshing} className="text-muted-foreground" data-testid="m-inbox-refresh">
          <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} />
        </button>
      </div>

      {rows === null && <div className="text-sm text-muted-foreground py-10 text-center">Loading…</div>}
      {rows && rows.length === 0 && (
        <div className="text-center text-sm text-muted-foreground py-12" data-testid="m-inbox-empty">
          <Mail className="w-8 h-8 mx-auto mb-2 opacity-40" />
          No lead replies waiting. You're all caught up.
        </div>
      )}

      {rows && rows.map((r) => (
        <button key={r.id} onClick={() => openEmail(r)}
          className="w-full text-left bg-card border border-border rounded-xl p-3.5 active:bg-muted/40"
          data-testid={`m-inbox-row-${r.id}`}>
          <div className="flex items-center justify-between gap-2">
            <span className="text-sm font-medium truncate">{r.prospect_name || r.from_email}</span>
            <span className="text-[0.65rem] text-muted-foreground shrink-0">{timeAgo(r.received_at)}</span>
          </div>
          <div className="text-xs text-foreground/80 truncate mt-0.5">{r.subject}</div>
          <div className="text-[0.72rem] text-muted-foreground line-clamp-2 mt-1">{r.body_preview}</div>
        </button>
      ))}
    </div>
  );
}
